import React, { useLayoutEffect, useMemo, useState, } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Box, Icon, Spinner, Text, useFormControl } from "@chakra-ui/react";
import { useSelect } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { SelectOption } from "./select-option";
import { SelectOptionAccessory } from "./select-option-accessory";
import { usePopover, Popover } from "./controlled-popover";
const defaultIsOptionDisabled = () => false;
function defaultOptionLabel(option) {
    return String(option.label ?? "");
}
function defaultOptionValue(option) {
    return option.value;
}
export function Select({ isDisabled = false, isLoading = false, isInvalid = false, isOptionDisabled = defaultIsOptionDisabled, options, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, ...props }) {
    const formControl = useFormControl({
        isDisabled: isDisabled || isLoading,
        isInvalid,
    });
    const { getTargetProps, getPopoverProps, update } = usePopover();
    const [isOpenScheduled, setIsOpenScheduled] = useState(false);
    const selectedOption = useMemo(() => {
        if (!value) {
            return;
        }
        return options.find((option) => {
            return isEqual(optionValue(option), value);
        });
    }, [options, value]);
    const { isOpen, highlightedIndex, getToggleButtonProps, getMenuProps, getItemProps, openMenu, } = useSelect({
        labelId: `${formControl.id}-label`,
        toggleButtonId: formControl.id,
        items: options,
        itemToString(option) {
            return option ? optionLabel(option) : "";
        },
        stateReducer(state, { changes }) {
            // Don't open and focus the menu immediately and schedule it for later.
            // Popover position must be recalculated first, otherwise Downshift
            // focuses the popover in its old position, causing the page to scroll
            // to the wrong position.
            // See https://floating-ui.com/docs/react-dom#effects.
            if (!state.isOpen && changes.isOpen) {
                if (!isOpenScheduled) {
                    update();
                    setIsOpenScheduled(true);
                    return state;
                }
                setIsOpenScheduled(false);
            }
            return changes;
        },
        selectedItem: selectedOption ?? null,
        onSelectedItemChange({ selectedItem }) {
            if (selectedItem) {
                onChange(optionValue(selectedItem));
            }
        },
    });
    useLayoutEffect(() => {
        if (!isOpenScheduled) {
            return;
        }
        requestAnimationFrame(() => {
            openMenu();
        });
    }, [isOpenScheduled]);
    let hoverBorderColor = "gray.500";
    if (isOpen) {
        hoverBorderColor = "gray.400";
    }
    if (isInvalid) {
        hoverBorderColor = "red.600";
    }
    return (React.createElement(RemoveScroll, { enabled: isOpen },
        React.createElement(Box, { as: "button", "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], disabled: formControl.disabled, display: "flex", alignItems: "center", border: "1px", borderColor: isInvalid ? "red.600" : "gray.400", borderRadius: "md", transition: "200ms all", width: width, maxWidth: "100%", height: "36px", px: 3, outline: "none", bg: "white", type: "button", _disabled: isLoading
                ? {
                    cursor: "wait",
                    _hover: {
                        borderColor: "gray.400",
                    },
                }
                : {
                    cursor: "not-allowed",
                    opacity: 0.4,
                    _hover: {
                        borderColor: "gray.400",
                    },
                }, _hover: {
                borderColor: hoverBorderColor,
            }, _focus: {
                borderColor: isInvalid ? "red.600" : "brand.600",
                // Fix app styles
                outline: "none !important",
            }, _focusVisible: {
                boxShadow: "outline",
            }, ...getToggleButtonProps({
                ...getTargetProps({
                    onFocus: formControl.onFocus,
                    onBlur: formControl.onBlur,
                }),
            }), ...props },
            selectedOption && typeof optionAccessory === "function" && (React.createElement(SelectOptionAccessory, { accessory: optionAccessory(selectedOption) })),
            React.createElement(Text, { as: "span", flex: "1", minWidth: 0, fontSize: "md", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", color: value ? "gray.800" : "gray.500", mr: 3 }, selectedOption ? optionLabel(selectedOption) : placeholder),
            isLoading ? (React.createElement(Spinner, { color: "brand.500", emptyColor: "gray.200", size: "sm", speed: "440ms", thickness: "2px" })) : (React.createElement(Icon, { as: ChevronDownIcon, boxSize: 4 }))),
        React.createElement(Popover, { isOpen: isOpen, ...getMenuProps(getPopoverProps()) },
            options.length === 0 && (React.createElement(Box, { py: 3, textAlign: "center" },
                React.createElement(Text, { color: "gray.600" }, "No options"))),
            options.length > 0 &&
                options.map((option, index) => (React.createElement(SelectOption, { key: optionLabel(option), isFocused: index === highlightedIndex, isSelected: option === selectedOption, isDisabled: isOptionDisabled(option), label: optionLabel(option), accessory: typeof optionAccessory === "function"
                        ? optionAccessory(option)
                        : undefined, itemProps: getItemProps({
                        item: option,
                        index,
                        disabled: isOptionDisabled(option),
                    }) }))))));
}
